import Vue from 'vue';
import Cookies from 'js-cookie';
import parser from 'accept-language-parser';
import i18next from 'i18next';
import VueI18Next, { VueI18NextOptions } from '@panter/vue-i18next';
import { AppConfig } from '../i18n.interfaces';
import enGB from '../i18n/en-GB.json';
import enCA from '../i18n/en-CA.json';
import enUS from '../i18n/en-US.json';
import frCA from '../i18n/fr-CA.json';
import arSA from '../i18n/ar-SA.json';

Vue.use(VueI18Next);

interface CustomNavigator extends Navigator {
  readonly cypressLanguage?: string;
  readonly browserLanguage?: string;
  readonly userLanguage?: string;
  readonly systemLanguage?: string;
}

interface LocaleConfig {
  browserLanguagePropertyKeys: (keyof CustomNavigator)[];
  origins: {
    [lang: string]: string;
  };
}

// These window.navigator contain language information
// 1. languages
//      -> Array of preferred languages (eg ["en-US", "zh-CN", "ja-JP"]) Firefox^32, Chrome^32
// 2. language
//      -> Preferred language as String (eg "en-US") Firefox^5, IE^11, Safari,
//                 Chrome sends Browser UI language
// 3. browserLanguage -> UI Language of IE
// 4. userLanguage    -> Language of Windows Regional Options
// 5. systemLanguage  -> UI Language of Windows
const localeConfig: LocaleConfig = {
  browserLanguagePropertyKeys: [
    'cypressLanguage',
    'languages',
    'language',
    'browserLanguage',
    'userLanguage',
    'systemLanguage'
  ],

  // Locales where first two letters aren't just same as last two (like de-DE)
  origins: {
    en: 'en-GB'
  }
};

function bestLocale(locale: string, availableLanguages: string[]): string {
  // Pick exact locale, if it exists
  if (availableLanguages.includes(locale)) {
    return locale;
  }

  // Pick origin country locale, if it exists (e.g. en-US -> en-GB)
  const lang = locale.slice(0, 2);
  const originLocale =
    localeConfig.origins[lang] || `${lang}-${lang.toUpperCase()}`;
  if (availableLanguages.includes(originLocale)) {
    return originLocale;
  }

  // Pick any available locale of the same language (or return null)
  return availableLanguages.reduce((chosen, available) => {
    if (available.slice(0, 2) === lang) {
      return available;
    }

    return chosen;
  }, null);
}

export function detectLocale(
  config: AppConfig,
  nav?: CustomNavigator | string
): string {
  const availableLanguages = config.locales.map((locale) => locale.code);

  if (!nav) {
    if (typeof window === 'undefined') {
      return availableLanguages[0];
    }

    nav = window.navigator;
  }

  const storedLocale = Cookies.get('sklang');
  if (storedLocale && availableLanguages.includes(storedLocale)) {
    return storedLocale;
  }

  if (typeof nav === 'string') {
    return bestLocale(nav, availableLanguages) || availableLanguages[0];
  }

  for (const property of localeConfig.browserLanguagePropertyKeys) {
    const value = nav[property];
    if (!value) {
      continue;
    }

    if (Array.isArray(value)) {
      for (const lang of value) {
        const best = bestLocale(lang, availableLanguages);
        if (best) {
          return best;
        }
      }
    } else if (typeof value === 'string') {
      const best = bestLocale(value, availableLanguages);
      if (best) {
        return best;
      }
    }
  }

  return availableLanguages[0];
}

export function detectLocaleServerSide(
  config: AppConfig,
  request: { headers: Record<string, string> }
): string {
  const availableLanguages = config.locales.map((locale) => locale.code);

  const skLangCookieName = 'sklang';
  const cookies = request.headers['cookie'];
  const matches = cookies?.match(
    `(^|;)\\s*${skLangCookieName}\\s*=\\s*([^;]+)`
  );
  // Cookies string looks like "_ga=GA1.2.887141706.1608031225; sklang=fr-CA"
  // Match send back an array of matches from the regex and the 3rd item is the cookie value
  const storedLocale = matches ? matches[2] : undefined;
  if (storedLocale && availableLanguages.includes(storedLocale)) {
    return storedLocale;
  }

  const browserLanguage = parser.pick(
    availableLanguages,
    request.headers['accept-language']
  );

  if (browserLanguage) {
    return browserLanguage;
  }

  return availableLanguages[0];
}

export let i18n: VueI18Next;

export default function init(
  config: AppConfig,
  locale: string = detectLocale(config),
  cb?: (newLocale: string, initial: boolean) => void
): VueI18Next {
  Vue.config.lang = locale;

  const lang = config.locales.find(({ code }) => code === locale);
  if (lang && lang.rtl && typeof document !== 'undefined') {
    document.body.dir = 'rtl';
  }

  const resources: i18next.Resource = {};

  const utilsConfig: i18next.Resource = {
    'en-GB': enGB,
    'en-CA': enCA,
    'en-US': enUS,
    'fr-CA': frCA,
    'ar-SA': arSA
  };

  config.locales.forEach((locale) => {
    resources[locale.code] = {
      translation: locale.translations,
      utils: utilsConfig[locale.code]
    };
  });

  i18next.init({
    lng: locale,
    fallbackLng: 'en-GB',
    resources
  });
  Vue.t = i18next.t.bind(i18next);

  // vue-i18next overrides $i18n already but it's a bit rubbish
  Vue.prototype.$skI18n = Vue.observable({
    locale,
    changeLanguage(lang: string): Promise<i18next.TranslationFunction> {
      return i18next.changeLanguage(lang);
    },
    availableLanguages: config.locales
  });

  if (typeof cb === 'function') {
    cb(locale, true);
  }

  const i18nextOptions: VueI18NextOptions = {
    bindI18n: '',
    bindStore: ''
  };

  // Don't add event listeners on the server!
  if (typeof window !== 'undefined') {
    i18nextOptions.bindI18n = 'languageChanged';

    i18next.on('languageChanged', (lang) => {
      // Using this.$skI18n is preferred as it is reactive
      Vue.prototype.$skI18n.locale = lang;
      Vue.config.lang = lang;

      Cookies.set('sklang', lang, { expires: 365, path: '/' });

      if (typeof cb === 'function') {
        cb(lang, false);
      }
    });
  }

  i18n = new VueI18Next(i18next, i18nextOptions);

  return i18n;
}
